/**
 * useTrainingEvents - Unified Training Event Pipeline
 * 
 * SINGLE SOURCE OF TRUTH for all training telemetry.
 * Replaces fragmented event logging across multiple hooks.
 * 
 * Event Taxonomy:
 * - impressed: artifact card visible in viewport
 * - opened: external link clicked, new tab opened
 * - engaged: meaningful return detected
 * - completed: completion confirmed via dialog
 * - abandoned: session timeout or explicit dismiss
 * - rated: feedback submitted
 * 
 * Usage:
 *   const { logOpenedSync, logCompleted } = useTrainingEvents();
 *   await logOpenedSync({ ... }, targetUrl);
 */

import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-J: route training-event retry-queue timestamps and engaged/
// completed lifecycle write timestamps through the canonical clock so
// effectiveness-event flow is deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import type { 
  CanonicalTrainingEventType, 
  TrainingEventSurface, 
  TrainingEventSource 
} from '@/types/training';

// =============================================
// EVENT RETRY QUEUE (for failed sync logs)
// =============================================

interface QueuedEvent {
  eventType: CanonicalTrainingEventType;
  options: LogEventOptions;
  timestamp: number;
  // FIX-R49-HIGH-1: per-event retry counter to cap re-queuing on
  // persistently failing inserts (e.g., RLS regression, schema drift).
  // Without a cap, a permanently failing event could re-enter the queue
  // forever, growing memory and re-firing the same insert every interval.
  retryCount?: number;
}

const eventRetryQueue: QueuedEvent[] = [];
const MAX_RETRY_QUEUE_SIZE = 50;
const MAX_PER_EVENT_RETRIES = 3;

function queueRetryEvent(event: QueuedEvent) {
  const nextRetryCount = (event.retryCount ?? 0) + 1;
  if (nextRetryCount > MAX_PER_EVENT_RETRIES) {
    console.warn('[TrainingEvents] Discarding event after max retries:', event.eventType);
    return;
  }
  if (eventRetryQueue.length >= MAX_RETRY_QUEUE_SIZE) {
    eventRetryQueue.shift(); // Remove oldest
  }
  eventRetryQueue.push({ ...event, retryCount: nextRetryCount });
  console.warn('[TrainingEvents] Queued event for retry:', event.eventType, '(attempt', nextRetryCount, ')');
}

// =============================================
// TYPES
// =============================================

export interface LogEventOptions {
  technicianProfileId: string;
  artifactId: string;
  source: TrainingEventSource;
  surface: TrainingEventSurface;
  ticketId?: string;
  jobIds?: string[];
  jobCategory?: string;
  sessionId?: string;
  rating?: number;
  confidenceDelta?: number;
  timeSpentSeconds?: number;
  metadata?: Record<string, unknown>;
}

export interface LoggedEvent {
  id: string;
  eventType: CanonicalTrainingEventType;
  createdAt: string;
}

// =============================================
// HOOK
// =============================================

export function useTrainingEvents() {
  const queryClient = useQueryClient();

  /**
   * Log a training event (async mutation).
   *
   * REVERT-R47-MED-2 (R49-CRIT-1): the previous `?? null` normalization on
   * `technician_profile_id` was INCORRECT — the column is `NOT NULL` per
   * `training_effectiveness_events` schema (types.ts:17588 confirms
   * `technician_profile_id: string`, not nullable). Inserting null would
   * have caused INSERT failures for passport-mode users instead of fixing
   * anything. The right behavior: skip the insert entirely when no
   * technician_profile_id is available (passport users have no
   * technician_profiles row, so there's nothing to attribute the event
   * to). The early-return below handles this; the field is then passed
   * through unchanged.
   */
  const logEventMutation = useMutation({
    mutationFn: async (params: LogEventOptions & { eventType: CanonicalTrainingEventType }) => {
      if (!params.technicianProfileId) {
        // Passport-mode user (no tech profile). Drop the event silently.
        // Logging at warn level so support can still trace via console.
        console.warn('[TrainingEvents] Skipping insert — no technician_profile_id');
        return null as unknown as LoggedEvent;
      }
      const { data, error } = await supabase
        .from('training_effectiveness_events')
        .insert({
          technician_profile_id: params.technicianProfileId,
          artifact_id: params.artifactId,
          event_type: params.eventType,
          source: params.source,
          surface: params.surface,
          ticket_id: params.ticketId,
          job_ids: params.jobIds,
          job_category: params.jobCategory,
          session_id: params.sessionId, // Real column for efficient joins
          rating: params.rating,
          confidence_delta: params.confidenceDelta,
          time_spent_seconds: params.timeSpentSeconds,
          metadata: {
            ...params.metadata,
            session_id: params.sessionId, // Keep in metadata for backward compatibility
          },
        })
        .select('id, event_type, created_at')
        .single();

      if (error) throw error;
      
      return {
        id: data.id,
        eventType: data.event_type as CanonicalTrainingEventType,
        createdAt: data.created_at,
      } as LoggedEvent;
    },
    onSuccess: (_, variables) => {
      // Invalidate effectiveness events queries
      queryClient.invalidateQueries({
        queryKey: ['effectiveness-events', variables.technicianProfileId],
      });
    },
  });

  /**
   * Log 'impressed' event - artifact card visible in viewport
   */
  const logImpressed = (options: LogEventOptions) => {
    logEventMutation.mutate({ ...options, eventType: 'impressed' });
  };

  /**
   * Log 'opened' event using fire-and-forget pattern
   * 
   * CRITICAL: The window must be opened BEFORE calling this function.
   * Call window.open() immediately in click handler, then call this.
   * This avoids popup blocking in modern browsers.
   * 
   * @param options - Event options including sessionId
   */
  const logOpenedFireAndForget = (options: LogEventOptions): void => {
    // REVERT-R47-MED-2 (R49-CRIT-1): same correction as logEventMutation —
    // the column is NOT NULL; skip when no technician_profile_id rather
    // than inserting null. Passport users won't have training opens
    // logged (acceptable: there's no tech identity to attribute against).
    if (!options.technicianProfileId) {
      console.warn('[TrainingEvents] Skipping logOpenedFireAndForget — no technician_profile_id');
      return;
    }
    // Fire-and-forget using Promise wrapper for proper error handling
    Promise.resolve(
      supabase
        .from('training_effectiveness_events')
        .insert({
          technician_profile_id: options.technicianProfileId,
          artifact_id: options.artifactId,
          event_type: 'opened',
          source: options.source,
          surface: options.surface,
          ticket_id: options.ticketId,
          job_ids: options.jobIds,
          job_category: options.jobCategory,
          session_id: options.sessionId, // Real column
          metadata: {
            ...options.metadata,
            session_id: options.sessionId, // Also in metadata for compatibility
            open_logged: true,
          },
        })
    )
      .then(({ error }) => {
        if (error) {
          console.error('[TrainingEvents] Failed to log opened:', error);
          queueRetryEvent({
            eventType: 'opened',
            options,
            timestamp: clock.nowMs(),
          });
        }
      })
      .catch((error) => {
        console.error('[TrainingEvents] Exception logging opened:', error);
        queueRetryEvent({
          eventType: 'opened',
          options,
          timestamp: clock.nowMs(),
        });
      });

    // Invalidate queries (fire-and-forget)
    queryClient.invalidateQueries({
      queryKey: ['effectiveness-events', options.technicianProfileId],
    });
  };

  /**
   * Log 'engaged' event - meaningful return detected
   * 
   * Engaged fires when:
   * 1. User previously triggered 'opened' for this artifact
   * 2. User returns to OptiBay (visibility change detected)
   * 3. User does at least one of:
   *    - Spends >=30s on completion dialog
   *    - Submits >=1 reflection prompt response
   *    - Clicks "Partial" or "Complete" in confirmation dialog
   */
  const logEngaged = (options: LogEventOptions) => {
    logEventMutation.mutate({ ...options, eventType: 'engaged' });
  };

  /**
   * Log 'completed' event - completion confirmed via dialog
   */
  const logCompleted = (options: LogEventOptions) => {
    logEventMutation.mutate({ ...options, eventType: 'completed' });
  };

  /**
   * Log 'abandoned' event - session timeout or explicit dismiss
   */
  const logAbandoned = (options: LogEventOptions) => {
    logEventMutation.mutate({ ...options, eventType: 'abandoned' });
  };

  /**
   * Log 'rated' event - feedback submitted with rating and confidence delta
   */
  const logRated = (options: LogEventOptions & { rating: number; confidenceDelta?: number }) => {
    logEventMutation.mutate({ ...options, eventType: 'rated' });
  };

  /**
   * Process retry queue - call periodically to retry failed events
   */
  const processRetryQueue = async () => {
    if (eventRetryQueue.length === 0) return;

    const events = [...eventRetryQueue];
    eventRetryQueue.length = 0; // Clear queue

    for (const event of events) {
      // REVERT-R48-HIGH-1 (R49-CRIT-1): the column is NOT NULL, so a
      // queued event missing technician_profile_id can never be
      // inserted — drop it from the retry queue entirely instead of
      // looping forever on failed inserts.
      if (!event.options.technicianProfileId) {
        console.warn('[TrainingEvents] Discarding queued event — no technician_profile_id');
        continue;
      }
      try {
        await supabase
          .from('training_effectiveness_events')
          .insert({
            technician_profile_id: event.options.technicianProfileId,
            artifact_id: event.options.artifactId,
            event_type: event.eventType,
            source: event.options.source,
            surface: event.options.surface,
            ticket_id: event.options.ticketId,
            job_ids: event.options.jobIds,
            job_category: event.options.jobCategory,
            session_id: event.options.sessionId, // Real column
            metadata: {
              ...event.options.metadata,
              session_id: event.options.sessionId,
              retried: true,
              original_timestamp: event.timestamp,
            },
          });
        console.log('[TrainingEvents] Retry succeeded:', event.eventType);
      } catch (error) {
        console.error('[TrainingEvents] Retry failed:', error);
        // Re-queue if still failing
        queueRetryEvent(event);
      }
    }
  };

  /**
   * Log 'engaged' event with ATOMIC idempotency check
   * Uses single UPDATE to prevent race conditions from tab focus/blur loops
   * 
   * @returns true if event was logged, false if already engaged
   */
  const logEngagedAtomic = async (
    sessionId: string,
    options: Omit<LogEventOptions, 'sessionId'>
  ): Promise<boolean> => {
    // ATOMIC: Update only if engaged_at IS NULL
    const { data, error } = await supabase
      .from('training_consumption_sessions')
      .update({ engaged_at: clock.now().toISOString() })
      .eq('id', sessionId)
      .is('engaged_at', null)
      .select('engaged_at')
      .single();
    
    if (error || !data) {
      console.log('[TrainingEvents] Engaged already logged or session not found:', sessionId);
      return false;
    }
    
    // Successfully set engaged_at - now log the event
    logEventMutation.mutate({
      ...options,
      sessionId,
      eventType: 'engaged',
    });
    
    return true;
  };

  /**
   * Log 'completed' or 'abandoned' with ATOMIC idempotency check
   * Uses single UPDATE to prevent race conditions
   * 
   * @returns true if event was logged, false if already completed
   */
  const logCompletionAtomic = async (
    sessionId: string,
    eventType: 'completed' | 'abandoned',
    options: Omit<LogEventOptions, 'sessionId'>
  ): Promise<boolean> => {
    // ATOMIC: Update only if completed_at IS NULL
    const { data, error } = await supabase
      .from('training_consumption_sessions')
      .update({ 
        completed_at: clock.now().toISOString(),
        completion_status: eventType === 'abandoned' ? 'abandoned' : 'completed',
      })
      .eq('id', sessionId)
      .is('completed_at', null)
      .select('completed_at')
      .single();
    
    if (error || !data) {
      console.log('[TrainingEvents] Completion already logged or session not found:', sessionId);
      return false;
    }
    
    // Successfully set completed_at - now log the event
    logEventMutation.mutate({
      ...options,
      sessionId,
      eventType,
    });
    
    return true;
  };

  // FIX-R49-HIGH-1: drive processRetryQueue on a 30-second interval so
  // queued events actually get retried. Previously the function was
  // exported but never called from anywhere — failed events sat in the
  // module-level eventRetryQueue array indefinitely (effectively dead
  // code). Cleared on hook unmount; idempotent if multiple instances of
  // the hook mount (the queue is module-scoped, not per-hook).
  useEffect(() => {
    const intervalId = setInterval(() => {
      void processRetryQueue();
    }, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  return {
    // Event logging functions
    logImpressed,
    logOpenedFireAndForget, // Renamed: fire-and-forget pattern for popup safety
    logEngaged,
    logEngagedAtomic, // New: atomic idempotent version
    logCompleted,
    logCompletionAtomic, // New: atomic idempotent version
    logAbandoned,
    logRated,

    // Utility
    processRetryQueue,

    // Mutation state
    isLogging: logEventMutation.isPending,
    logError: logEventMutation.error,
  };
}
